// ============================================================================
// EMISSION OFFSETS
// ============================================================================
// Carbon offset credit register
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { emissionsApi } from '../api/emissions.api';
import PageInfoButton from '@/components/PageInfoButton';

interface Offset {
  id: number;
  name: string;
  registry: string;
  project_id?: string;
  project_type?: string;
  vintage_year: number;
  quantity_tco2e: number;
  price_per_tco2e?: number;
  country?: string;
  verification_status?: string;
  notes?: string;
}

interface CreateOffsetForm {
  name: string;
  registry: string;
  project_id: string;
  project_type: string;
  vintage_year: string;
  quantity_tco2e: string;
  price_per_tco2e: string;
  country: string;
  verification_status: string;
  notes: string;
}

const DEFAULT_FORM: CreateOffsetForm = {
  name: '',
  registry: 'Gold Standard',
  project_id: '',
  project_type: '',
  vintage_year: '',
  quantity_tco2e: '',
  price_per_tco2e: '',
  country: '',
  verification_status: 'verified',
  notes: '',
};

const REGISTRIES = ['Gold Standard', 'VCS', 'CDM', 'CAR', 'ACR', 'Plan Vivo', 'other'];

const registryColors: Record<string, string> = {
  'Gold Standard': 'bg-yellow-100 text-yellow-800',
  VCS: 'bg-green-100 text-green-800',
  CDM: 'bg-blue-100 text-blue-700',
  CAR: 'bg-purple-100 text-purple-700',
  ACR: 'bg-indigo-100 text-indigo-700',
  'Plan Vivo': 'bg-teal-100 text-teal-700',
  other: 'bg-slate-100 text-slate-700',
};

const EmissionOffsets: React.FC = () => {
  const [offsets, setOffsets] = useState<Offset[]>([]);
  const [_stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreateOffsetForm>(DEFAULT_FORM);

  const loadData = useCallback(async () => {
    try {
      const [data, statsData] = await Promise.all([
        emissionsApi.getOffsets(),
        emissionsApi.getOffsetStats(),
      ]);
      setOffsets(Array.isArray(data) ? data : []);
      setStats(statsData);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load offsets');
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadData();
      setLoading(false);
    };
    init();
  }, [loadData]);

  const createOffset = async () => {
    if (!form.name.trim()) return;
    try {
      const payload: Parameters<typeof emissionsApi.createOffset>[0] = {
        name: form.name,
        registry: form.registry,
        vintage_year: Number(form.vintage_year),
        quantity_tco2e: Number(form.quantity_tco2e),
      };
      if (form.project_id) payload.project_id = form.project_id;
      if (form.project_type) payload.project_type = form.project_type;
      if (form.price_per_tco2e) payload.price_per_tco2e = Number(form.price_per_tco2e);
      if (form.country) payload.country = form.country;
      if (form.verification_status) payload.verification_status = form.verification_status;
      if (form.notes) payload.notes = form.notes;
      await emissionsApi.createOffset(payload);
      setShowCreate(false);
      setForm(DEFAULT_FORM);
      await loadData();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create offset');
    }
  };

  const deleteOffset = async (id: number) => {
    try {
      await emissionsApi.deleteOffset(id);
      await loadData();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete offset');
    }
  };

  const totalCredits = offsets.length;
  const totalTco2e = offsets.reduce((sum, o) => sum + o.quantity_tco2e, 0);
  const byRegistry = offsets.reduce((acc, o) => {
    acc[o.registry] = (acc[o.registry] ?? 0) + o.quantity_tco2e;
    return acc;
  }, {} as Record<string, number>);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Offset Credits</h1><PageInfoButton /></div>
          <p className="text-slate-500 mt-1">Carbon offset credit register</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
        >
          + Add Offset
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Total Credits</p>
          <p className="text-3xl font-bold mt-1 text-slate-900">{totalCredits}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Total tCO₂e</p>
          <p className="text-3xl font-bold mt-1 text-emerald-700">
            {(totalTco2e || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-white border rounded-xl p-4 col-span-2 lg:col-span-1">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">By Registry</p>
          <div className="space-y-1">
            {Object.entries(byRegistry).map(([reg, qty]) => (
              <div key={reg} className="flex justify-between text-xs">
                <span className={`px-2 py-0.5 rounded font-medium ${registryColors[reg] ?? 'bg-slate-100 text-slate-600'}`}>
                  {reg}
                </span>
                <span className="text-slate-600">{(qty || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} t</span>
              </div>
            ))}
            {Object.keys(byRegistry).length === 0 && (
              <p className="text-slate-400 text-xs">No data</p>
            )}
          </div>
        </div>
      </div>

      {/* Offsets Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-sm font-semibold text-slate-700">All Offset Credits</h2>
        </div>
        {offsets.length === 0 ? (
          <div className="px-6 py-8 text-center text-slate-400 text-sm">
            No offset credits registered yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Registry</th>
                  <th className="px-4 py-3 text-left">Vintage Year</th>
                  <th className="px-4 py-3 text-right">Quantity (tCO₂e)</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {offsets.map(o => (
                  <tr key={o.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-left font-medium text-slate-800">
                      {o.name}
                      {o.country && <span className="ml-2 text-xs text-slate-400">{o.country}</span>}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${registryColors[o.registry] ?? 'bg-slate-100 text-slate-600'}`}>
                        {o.registry}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left text-slate-600">{o.vintage_year}</td>
                    <td className="px-4 py-3 text-left font-semibold text-slate-800">
                      {(Number(o.quantity_tco2e) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-left">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        o.verification_status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {o.verification_status ?? 'unverified'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left">
                      <button
                        onClick={() => deleteOffset(o.id)}
                        className="px-2 py-1 text-xs border border-red-200 rounded text-red-600 hover:bg-red-50 transition-colors"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Offset Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Add Offset Credit</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="e.g. Amazon Reforestation Credits"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Registry *</label>
                  <select
                    value={form.registry}
                    onChange={e => setForm(f => ({ ...f, registry: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {REGISTRIES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Project Type</label>
                  <input
                    type="text"
                    value={form.project_type}
                    onChange={e => setForm(f => ({ ...f, project_type: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="e.g. Reforestation"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Project ID</label>
                  <input
                    type="text"
                    value={form.project_id}
                    onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="e.g. GS-1234"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                  <input
                    type="text"
                    value={form.country}
                    onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="e.g. Brazil"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vintage Year *</label>
                  <input
                    type="number"
                    value={form.vintage_year}
                    onChange={e => setForm(f => ({ ...f, vintage_year: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="2023"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Quantity (tCO₂e) *</label>
                  <input
                    type="number"
                    step="any"
                    value={form.quantity_tco2e}
                    onChange={e => setForm(f => ({ ...f, quantity_tco2e: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="100"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Price per tCO₂e ($)</label>
                  <input
                    type="number"
                    step="any"
                    value={form.price_per_tco2e}
                    onChange={e => setForm(f => ({ ...f, price_per_tco2e: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="15.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Verification Status</label>
                  <select
                    value={form.verification_status}
                    onChange={e => setForm(f => ({ ...f, verification_status: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="verified">Verified</option>
                    <option value="pending">Pending</option>
                    <option value="unverified">Unverified</option>
                    <option value="retired">Retired</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowCreate(false); setForm(DEFAULT_FORM); }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createOffset}
                disabled={!form.name.trim()}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                Add Offset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmissionOffsets;
